import React from "react";
import {
  StyledImpersonateContainer,
  StyledPersonalAccountContainer,
  StyledUserPanel,
} from "./UserPanel.styled";
import { useAppSelector } from "../../hooks/store.hook";
import {
  Avatar,
  AvatarDimensionsEnum,
  AvatarDirectionEnum,
} from "../core/Avatar/Avatar";
import { ReactComponent as UserIcon } from "../../icons/user.svg";
import { ReactComponent as UserImpersonateIcon } from "../../icons/impersonateUser.svg";
import { FontWeightEnum, PageNames } from "../../types/AppTypes";
import { BodyTyp, CaptionsTyp } from "../Typography/Typography";
import { theme } from "../../constants/StyledComponents";
import { Link, LinkSizeEnum } from "../core/Link/Link";
import { useNavigate } from "react-router-dom";
import {
  ASSISTANCE_LABEL,
  EMPTY_COLLABORATORS_MESSAGE,
  EMPTY_STRING,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
  //FAQS_LABEL,
  IMPERSONATE_LABEL,
  LOGOUT_LABEL,
  PERSONAL_ACCOUNT_LABEL,
  PRIVACY_POLICY_LABEL,
  SEARCH_USER_LABEL,
} from "../../constants/Dictionary";
import { Divider } from "../core/Divider/Divider";
//import { ReactComponent as FAQIcon } from "../../icons/faqs.svg";
import { ReactComponent as TicketIcon } from "../../icons/ticket.svg";
import { ReactComponent as PrivacyIcon } from "../../icons/privacy.svg";
import { ReactComponent as LogoutIcon } from "../../icons/logout.svg";
import { getInitials } from "../../utils/collaborators.utils";
import { findLabelRoleByValue } from "../../utils/orgFunctions.utils";
import { useEditing } from "../../providers/editing.provider";
import { Accordion, AccordionVariantEnum } from "../core/Accordion/Accordion";
import SearchInput from "../core/SearchInput/SearchInput";
import CollaboratorsImpersonateService from "../../services/api/collaborators/CollaboratorsImpersonate.service";
import { useImpersonateCollaborators } from "../../hooks/userPanel.hook";
import { useNotification } from "../../hooks/notification.hook";
import UserChip from "../UserChip/UserChip";
import { StyledAvatarLayout } from "../core/Avatar/Avatar.styled";
import { useQueryClient } from "@tanstack/react-query";
import { UserPreferenceEnum } from "../../types/api/auth.type";

interface UserPanelProps {
  visible: boolean;
  setVisible: (x: boolean) => void;
  loadAppData: () => Promise<void>;
  setDataLoaded: (loaded: boolean) => void;
  logout: () => void;
}

export const UserPanel = ({
  visible,
  setVisible,
  loadAppData,
  setDataLoaded,
  logout,
}: UserPanelProps) => {
  const { notification } = useNotification();
  const { profile, parentUser } = useAppSelector((state) => state.user);
  const { orgSettings } = useAppSelector((state) => state.config);
  const { confirmLeave, edit } = useEditing();
  const queryClient = useQueryClient();

  const name = profile.name;
  const surname = profile.surname;

  const navigate = useNavigate();

  const shouldHideRole = (
    userPreferences: UserPreferenceEnum[] | null,
  ): boolean =>
    userPreferences?.includes(UserPreferenceEnum.HIDE_ROLE) ?? false;

  const handleLogout = () => {
    edit ? confirmLeave(logout) : logout();
    setVisible(false);
  };

  const userPanelLinks: {
    label: string;
    link: string;
    icon: React.ReactNode;
  }[] = [
    /* {
      label: FAQS_LABEL,
      link: PageNames.FAQ,
      icon: <FAQIcon />,
    }, */
    {
      label: ASSISTANCE_LABEL,
      link: "https://support.portal.abstract.it/servicedesk/customer/portal/3113",
      icon: <TicketIcon />,
    },
    {
      label: PRIVACY_POLICY_LABEL,
      link: PageNames.PRIVACY_POLICY,
      icon: <PrivacyIcon />,
    },
  ];

  const impersonateLabel = (
    <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD}>{IMPERSONATE_LABEL}</BodyTyp>
  );

  const {
    data: collaboratorsOptions,
    initLoading: collaboratorsInitLoading,
    canLoadMore: canLoadMoreCollaborators,
    fetchMore: fetchMoreCollaborators,
    clearSearch: clearCollaboratorsSearch,
    setSearch: setCollaboratorsSearchValue,
    search: collaboratorsSearchValue,
    fetchingMore: fetchingMoreCollaborators,
  } = useImpersonateCollaborators({
    enabled: visible && profile.enableImpersonate,
  });

  const searchOptions = collaboratorsOptions
    ? collaboratorsOptions.map((option) => ({
        value: option.email,
        label: (
          <UserChip
            id={option.id}
            name={option.name}
            surname={option.surname}
            role={
              findLabelRoleByValue(orgSettings.userRoles, option.role) ??
              EMPTY_STRING
            }
          />
        ),
      }))
    : [];

  const startImpersonate = (email: string) => {
    const action = async () => {
      try {
        setDataLoaded(false);
        await CollaboratorsImpersonateService.startImpersonate(email);
        await queryClient.invalidateQueries();
        await loadAppData();
        navigate("/");
      } catch (error) {
        console.error(error);
        notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
      }
    };

    edit ? confirmLeave(action) : action();
  };

  const endImpersonate = () => {
    const action = async () => {
      try {
        setDataLoaded(false);
        await CollaboratorsImpersonateService.endImpersonate();
        await queryClient.invalidateQueries();
        await loadAppData();
        navigate("/");
      } catch (error) {
        console.error(error);
        notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
      }
    };

    edit ? confirmLeave(action) : action();
  };

  return (
    <StyledUserPanel>
      <StyledAvatarLayout $direction={AvatarDirectionEnum.VERTICAL}>
        <Avatar
          initials={getInitials(profile.name, profile.surname)}
          variant={AvatarDimensionsEnum.BIG}
          icon={parentUser ? <UserImpersonateIcon /> : <UserIcon />}
          collaboratorId={profile.id}
          withBorder
          badgeColor={parentUser ? theme.colors.grey_2 : theme.colors.purple}
        />
        <div className="avatar-name-text">
          <BodyTyp fontWeight={FontWeightEnum.MEDIUM} variant="1">
            {`${surname} ${name}`}
          </BodyTyp>
          {!shouldHideRole(profile.userPreferences) ? (
            <CaptionsTyp
              fontWeight={FontWeightEnum.SEMIBOLD}
              color={parentUser ? theme.colors.grey_2 : theme.colors.purple}
            >
              {findLabelRoleByValue(orgSettings.userRoles, profile.role) ??
                EMPTY_STRING}
            </CaptionsTyp>
          ) : null}
        </div>
      </StyledAvatarLayout>
      {profile.enableImpersonate ? (
        <>
          <Divider />
          <StyledImpersonateContainer>
            <Accordion
              label={impersonateLabel}
              variant={AccordionVariantEnum.SECONDARY}
            >
              <div className="impersonate-search">
                <SearchInput
                  value={collaboratorsSearchValue}
                  onChange={setCollaboratorsSearchValue}
                  label={SEARCH_USER_LABEL}
                  name={"search"}
                  options={searchOptions}
                  loading={collaboratorsInitLoading}
                  canLoadMore={canLoadMoreCollaborators}
                  fetchMore={fetchMoreCollaborators}
                  onClear={clearCollaboratorsSearch}
                  fetchingMore={fetchingMoreCollaborators}
                  optionsEmptyMessage={EMPTY_COLLABORATORS_MESSAGE}
                  onSelectOption={startImpersonate}
                />
              </div>
            </Accordion>
          </StyledImpersonateContainer>
        </>
      ) : null}
      {parentUser ? (
        <>
          <Divider />
          <StyledPersonalAccountContainer>
            <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="2">
              {PERSONAL_ACCOUNT_LABEL}
            </BodyTyp>
            <UserChip
              id={parentUser.id}
              name={parentUser.name}
              surname={parentUser.surname}
              role={
                findLabelRoleByValue(orgSettings.userRoles, parentUser.role) ??
                EMPTY_STRING
              }
              onClick={endImpersonate}
              isLoggedUser
            />
          </StyledPersonalAccountContainer>
        </>
      ) : null}
      <Divider />
      {userPanelLinks.map((item, index) => {
        const handleClick = () => {
          const isExternal = item.link.startsWith("http");
          const action = isExternal
            ? () => window.open(item.link, "_blank")
            : () => navigate(item.link);

          edit ? confirmLeave(action) : action();
          setVisible(false);
        };

        return (
          <div className="link-list" key={`${item.label}${index}`}>
            <Link
              onClick={handleClick}
              underline
              icon={item.icon}
              size={LinkSizeEnum.SMALL}
            >
              <BodyTyp fontWeight={FontWeightEnum.REGULAR} variant="3">
                {item.label}
              </BodyTyp>
            </Link>
          </div>
        );
      })}
      <Divider />
      <Link
        onClick={handleLogout}
        color={theme.colors.purple}
        icon={<LogoutIcon />}
        size={LinkSizeEnum.SMALL}
      >
        <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="2">
          {LOGOUT_LABEL}
        </BodyTyp>
      </Link>
    </StyledUserPanel>
  );
};
